import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import macha.sh.kotlin.components.layouts.MarkdownLayout as _macha_sh_kotlin_components_layouts_MarkdownLayout
import macha.sh.kotlin.components.layouts.PageLayout as _macha_sh_kotlin_components_layouts_PageLayout
import macha.sh.kotlin.pages.AboutPage as _macha_sh_kotlin_pages_AboutPage
import macha.sh.kotlin.pages.BlogGuidesPage as _macha_sh_kotlin_pages_BlogGuidesPage
import macha.sh.kotlin.pages.ContactPage as _macha_sh_kotlin_pages_ContactPage
import macha.sh.kotlin.pages.FiveDollaHollaRedirect as _macha_sh_kotlin_pages_FiveDollaHollaRedirect
import macha.sh.kotlin.pages.HomePage as _macha_sh_kotlin_pages_HomePage
import macha.sh.kotlin.pages.ReposPage as _macha_sh_kotlin_pages_ReposPage
import macha.sh.kotlin.pages.blogguides.BlogPostPage as _macha_sh_kotlin_pages_blogguides_BlogPostPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Macha.sh"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "macha.sh.kotlin.components.layouts.MarkdownLayout",
            parentLayoutId = "macha.sh.kotlin.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                macha.sh.kotlin.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _macha_sh_kotlin_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "macha.sh.kotlin.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _macha_sh_kotlin_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "macha.sh.kotlin.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                macha.sh.kotlin.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _macha_sh_kotlin_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "macha.sh.kotlin.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                macha.sh.kotlin.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _macha_sh_kotlin_pages_AboutPage()
        }
        ctx.router.register(
            "/blog-guides",
            layoutId = "macha.sh.kotlin.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                macha.sh.kotlin.pages.initBlogGuidesPage(ctx)
            },
        ) { pageCtx -> 
            _macha_sh_kotlin_pages_BlogGuidesPage()
        }
        ctx.router.register(
            "/blogguides/post",
            layoutId = "macha.sh.kotlin.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                macha.sh.kotlin.pages.blogguides.initBlogPostPage(ctx)
            },
        ) { pageCtx -> 
            _macha_sh_kotlin_pages_blogguides_BlogPostPage()
        }
        ctx.router.register(
            "/contact",
            layoutId = "macha.sh.kotlin.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                macha.sh.kotlin.pages.initContactPage(ctx)
            },
        ) { pageCtx -> 
            _macha_sh_kotlin_pages_ContactPage()
        }
        ctx.router.register(
            "/five-dolla-holla",
            initRouteMethod = { ctx ->
                macha.sh.kotlin.pages.initContactPage(ctx)
            },
        ) { pageCtx -> 
            _macha_sh_kotlin_pages_FiveDollaHollaRedirect()
        }
        ctx.router.register(
            "/repos",
            layoutId = "macha.sh.kotlin.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                macha.sh.kotlin.pages.initReposPage(ctx)
            },
        ) { pageCtx -> 
            _macha_sh_kotlin_pages_ReposPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("markdown", macha.sh.kotlin.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", macha.sh.kotlin.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("footer", macha.sh.kotlin.components.sections.FooterStyle)
        ctx.theme.registerStyle("markdown-content", macha.sh.kotlin.components.widgets.MarkdownContentStyle)
        ctx.theme.registerStyle("headline-text", macha.sh.kotlin.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", macha.sh.kotlin.SubheadlineTextStyle)
        ctx.theme.registerVariant("-circle", macha.sh.kotlin.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", macha.sh.kotlin.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", macha.sh.kotlin.components.sections.SideMenuSlideInAnim)
        macha.sh.kotlin.initColorMode(ctx)
        macha.sh.kotlin.initStyles(ctx)
        macha.sh.kotlin.initSiteStyles(ctx)
        macha.sh.kotlin.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        macha.sh.kotlin.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
